import React from "react";
import {
    Button,
    Stack,
    IDropdownOption,
    mergeStyleSets,
    Label,
    ButtonGroup,
    Dropdown,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
} from "@nuorder/components";
import { formatMessage } from "lib/globalize-util";
import { ManageAccessPicker } from "./manage-access-picker";
import { comparePermissionRoles } from "containers/folders/utils/permissions";
import { ISharePersonaProps } from "../../../../types/manage-access";
import { useInfiniteFetchMembersInMerchant } from "../../../../hooks/merchants";
import siteConfig from "site-config";
import { fetchMoreDelayed, flattenInfiniteData } from "containers/folders/hooks/utils";
import { DATATEST } from "constants/integration";
import me from "models/me";
import { IdentityKind, ILowerBoundaryRoleMap, PermissionRole } from "containers/folders/types/base";
import {
    MANAGE_ACCESS_INPUT_BOX_MAX_WIDTH,
    MANAGE_ACCESS_COMFIRM_BOX_MAX_WIDTH,
    MANAGE_ACCESS_FOOTER_MAX_HEIGHT,
} from "containers/folders/utils/constants";
import { OverItemsLimitDialog } from "containers/folders/containers/space-manager/components/manage-access/over-item-limit-dialog";
import { WindowAnalytics } from "helpers/window-analytics";
import { AssortmentActions } from "constants/analytics";
export interface IManageAccessFormProps {
    merchantId: string;
    externalContactMerchantId?: string;
    itemId: string;
    itemPermissions: nuorder.folder.v1.Permission[];
    onDialogDismiss: () => void;
    onCreateItemPermission: (params) => void;
    LowerBoundaryRoleMap: ILowerBoundaryRoleMap;
    isExternalShare?: boolean;
    pickerLabel?: string;
    disableForm?: boolean;
    sharedItemMerchantId?: string;
    closeoutTargetMerchantId?: string;
}
const ITEM_LIMIT = 100;
export const generatePermissionOptions = (shouldExcludeOrganizerPermission: boolean): IDropdownOption[] => {
    return [
        ...(!shouldExcludeOrganizerPermission
            ? [{ key: PermissionRole.Organizer, text: formatMessage("Organizer") }]
            : []),
        { key: PermissionRole.FileOrganizer, text: formatMessage("File Organizer") },
        { key: PermissionRole.Writer, text: formatMessage("Writer") },
        { key: PermissionRole.Reader, text: formatMessage("Reader") },
    ];
};

export const ManageAccessForm: React.FC<IManageAccessFormProps> = ({
    merchantId,
    sharedItemMerchantId,
    externalContactMerchantId,
    itemId,
    onDialogDismiss,
    onCreateItemPermission,
    itemPermissions,
    LowerBoundaryRoleMap,
    isExternalShare,
    pickerLabel,
    disableForm,
    closeoutTargetMerchantId,
}) => {
    const [showOverLimit, setShowOverLimit] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>(undefined);
    const [selectedRole, setSelectedRole] = React.useState<PermissionRole>(PermissionRole.Reader);
    const [selectedPeople, setSelectedPeople] = React.useState<ISharePersonaProps[]>([]);
    const [overLimitErrorMessage, setOverLimitErrorMessage] = React.useState<string>(undefined);
    const contactMerchantId =
        externalContactMerchantId || sharedItemMerchantId || closeoutTargetMerchantId || merchantId;
    const {
        data: merchantData,
        isFetching: merchantIsFetching,
        isFetchingMore: merchantIsFetchingMore,
        fetchMore: merchantFetchMore,
        canFetchMore: merchantCanFetchMore,
    } = useInfiniteFetchMembersInMerchant(contactMerchantId, {}, { enabled: !!contactMerchantId });
    const NuSupportMerchantId = siteConfig.NUORDER_SUPPORT_MERCHANT_ID;
    const {
        data: nuSupportData,
        isFetching: nuSupportIsFetching,
        isFetchingMore: nuSupportIsFetchingMore,
        fetchMore: nuSupportFetchMore,
        canFetchMore: nuSupportCanFetchMore,
    } = useInfiniteFetchMembersInMerchant(NuSupportMerchantId, {}, { enabled: !!NuSupportMerchantId });

    const classes = React.useMemo(
        () =>
            mergeStyleSets({
                manageAccessFooter: {
                    maxHeight: `${MANAGE_ACCESS_FOOTER_MAX_HEIGHT}px`,
                },
                manageAccessContainer: {
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                },
                manageAccessBox: {
                    flex: "3",
                    marginRight: "24px",
                    maxWidth: `${MANAGE_ACCESS_INPUT_BOX_MAX_WIDTH}px`,
                },
                manageAccessComfirmBox: {
                    flex: "1",
                    maxWidth: `${MANAGE_ACCESS_COMFIRM_BOX_MAX_WIDTH}px`,
                },
                label: {
                    margin: "0 0 8px 0",
                },
                buttonGroup: {
                    marginTop: "24px !important",
                },
            }),
        []
    );

    const manageAccessPicker = React.useRef(null);

    const permissionOptions = generatePermissionOptions(isExternalShare);
    // merchantMembers
    const flattenMemberData = React.useMemo(
        () =>
            flattenInfiniteData(merchantData, (membersResponse) => {
                return membersResponse?.members?.filter((member) => member.member_user_id !== me._id);
            }),
        [merchantData]
    );
    React.useEffect(() => {
        fetchMoreDelayed(merchantCanFetchMore, merchantIsFetchingMore, merchantIsFetching, merchantFetchMore);
    }, [merchantCanFetchMore, merchantIsFetchingMore, merchantIsFetching, merchantFetchMore, merchantData]);

    // NuorderSupportMembers
    const flattenNuSupportMemberData = React.useMemo(
        () =>
            flattenInfiniteData(nuSupportData, (membersResponse) => {
                return membersResponse?.members;
            }),
        [nuSupportData]
    );
    React.useEffect(() => {
        fetchMoreDelayed(nuSupportCanFetchMore, nuSupportIsFetchingMore, nuSupportIsFetching, nuSupportFetchMore);
    }, [nuSupportCanFetchMore, nuSupportIsFetchingMore, nuSupportIsFetching, nuSupportFetchMore, nuSupportData]);

    React.useEffect(() => {
        const errorMsg = generateErrorMessage(selectedPeople, selectedRole, LowerBoundaryRoleMap);
        setErrorMessage(errorMsg);
    }, [selectedPeople, selectedRole, LowerBoundaryRoleMap]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChangeOverLimitErrorMessage = React.useCallback(
        (text: string) => {
            setOverLimitErrorMessage(text);
        },
        [setOverLimitErrorMessage]
    );
    const handelChangeOverLimitDialog = React.useCallback(
        (val: boolean) => {
            setShowOverLimit(val);
        },
        [setShowOverLimit]
    );

    const handleChangeRole = React.useCallback(
        (option) => {
            setSelectedRole(option);
        },
        [setSelectedRole]
    );
    const handleSelectedPeople = React.useCallback(
        (contact) => {
            setSelectedPeople(contact);
        },
        [setSelectedPeople]
    );

    const handleCloseDialog = React.useCallback(() => {
        manageAccessPicker?.current?.removeItems(selectedPeople);
        setSelectedPeople([]);
        onDialogDismiss();
    }, [onDialogDismiss, setSelectedPeople, manageAccessPicker, selectedPeople]);

    const generateErrorMessage = React.useCallback(
        (
            selectedPeople: ISharePersonaProps[],
            selectedRole: PermissionRole,
            LowerBoundaryRoleMap: ILowerBoundaryRoleMap
        ) => {
            let errorText = undefined;
            (selectedPeople || []).some((persona) => {
                const lowerBoundaryRole = LowerBoundaryRoleMap[persona.data.member_id];
                if (lowerBoundaryRole && comparePermissionRoles(selectedRole, lowerBoundaryRole, "<=")) {
                    errorText = `${persona.data.member_user_name} needs role higher than ${lowerBoundaryRole}`;
                    return;
                }
            });
            return errorText;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedPeople, selectedRole, LowerBoundaryRoleMap]
    );

    const handleGrantAccess = React.useCallback(() => {
        const validSelectedPeople = selectedPeople.filter((person) => person.isValid);
        if (validSelectedPeople.length) {
            const memberIds = validSelectedPeople.map((person) => person.data.member_id);
            // enable to add role for multiple person
            const memberMerchantIds = (validSelectedPeople || []).map((validPerson) => validPerson.data.merchant_id);
            onCreateItemPermission({
                merchantId,
                itemId,
                role: selectedRole,
                memberIds,
                memberMerchantIds, // NOTE: this is the merchantID for grantee
                kind: IdentityKind.MerchantMember,
            });
        }
        setSelectedPeople([]);

        WindowAnalytics.track(AssortmentActions.ShareAssortment, {
            userType: me.account_type,
            type: isExternalShare ? "External" : "Internal",
        });
    }, [selectedPeople, isExternalShare, selectedRole, setSelectedPeople, manageAccessPicker.current]); // eslint-disable-line react-hooks/exhaustive-deps

    const handelGetPickerRef = React.useCallback(
        (picker) => {
            manageAccessPicker.current = picker.current;
        },
        [manageAccessPicker]
    );
    // eslint-disable-next-line no-extra-boolean-cast
    const itemLimit = !!errorMessage ? selectedPeople.length : ITEM_LIMIT;
    const canFetchMore = merchantCanFetchMore || nuSupportCanFetchMore;
    const disableConfirm = disableForm || !!errorMessage || selectedPeople?.length === 0;
    const disablePicker = disableForm || canFetchMore;

    return (
        <Stack tokens={{ childrenGap: 16 }}>
            {errorMessage?.length && (
                <MessageBar
                    data-test={DATATEST.FOLDERS.GRANT_ACCESS.ERROR_MESSAGE_BAR}
                    messageBarType={MessageBarType.error}
                    isMultiline={true}
                >
                    {errorMessage}
                </MessageBar>
            )}
            {canFetchMore && (
                <Spinner
                    data-test={DATATEST.LOADING_CONTACTS_SPINNER}
                    size={SpinnerSize.small}
                    label={formatMessage("Loading Contacts...")}
                />
            )}
            <div className={classes.manageAccessContainer}>
                <div className={classes.manageAccessBox}>
                    {pickerLabel && <Label className={classes.label}>{pickerLabel}</Label>}
                    <ManageAccessPicker
                        data-test={DATATEST.FOLDERS.GRANT_ACCESS.MANAGE_ACCESS_PICKER}
                        disablePicker={disablePicker}
                        merchantMembers={flattenMemberData ?? []}
                        nuSupportMerchantMembers={flattenNuSupportMemberData}
                        itemPermissions={itemPermissions}
                        itemId={itemId}
                        onSelectedPeople={handleSelectedPeople}
                        selectedPeople={selectedPeople}
                        itemLimit={itemLimit}
                        getPickerRef={handelGetPickerRef}
                        onChangeOverLimitDialog={handelChangeOverLimitDialog}
                        onChangeOverLimitErrorMessage={handleChangeOverLimitErrorMessage}
                    />
                </div>
                <div className={classes.manageAccessComfirmBox}>
                    <Dropdown
                        label={formatMessage("Permissions:")}
                        testId={DATATEST.FOLDERS.GRANT_ACCESS.PERMISSIONS_DROPDOWN}
                        defaultSelectedKey={selectedRole}
                        selectedKey={selectedRole}
                        options={permissionOptions}
                        onChange={handleChangeRole}
                        styles={{ dropdown: { borderRadius: "4px", width: "90%" } }}
                    />
                </div>
            </div>
            <ButtonGroup className={classes.buttonGroup}>
                <Button
                    data-test={DATATEST.FOLDERS.DIALOG.CANCEL_BTN}
                    onClick={handleCloseDialog}
                    text={formatMessage("Cancel")}
                />
                <Button
                    primary={true}
                    data-test={DATATEST.FOLDERS.GRANT_ACCESS.GRANT_BTN}
                    disabled={disableConfirm}
                    text={formatMessage("Grant Access")}
                    onClick={handleGrantAccess}
                />
            </ButtonGroup>
            <OverItemsLimitDialog
                onOk={() => handelChangeOverLimitDialog(false)}
                open={showOverLimit}
                itemLimit={ITEM_LIMIT}
                message={overLimitErrorMessage}
            />
        </Stack>
    );
};
